<template>
    <div class="assignment-step">

        <template v-if="canViewStep">

            <div class="page-content">

                <a href="#" @click.prevent="showTeachertips(currentStep)" class="button-rounded button-rounded--teachertips " v-if="shouldShowTeacherTips"><span class="icon icon--info"></span>Tips</a>

                <component v-for="partial in partials" :is="partial.type + 'Partial'" :partial="partial" :readonly="readonly" :step="currentStep" :progress="progress" :key="partial.id" ref="partials"></component>

            </div>

            <nav class="action-nav">
                <hr class="flipped">
                <router-link v-if="currentStepIndex > 1" :to="prevStepLink" class="tag button-timid button-backward">Vorige stap</router-link>
                <primary-button :link="nextStepLink" :disabled="!canGoNext" @clicked="complete" :loading="loading" class="action-nav__next" >{{ buttonText }}</primary-button>
            </nav>

        </template>

    </div>
</template>


<script>

import step from './stepMixin'
import notify from 'js/components/notifications/notificationsMixin';

import { transform, logreject } from 'js/vuex/helpers';
import api from 'js/api/resources';
import { canTeach } from 'js/api/auth';

import track from 'js/helpers/analytics';

import HtmlPartial from './partials/HtmlPartial.vue';
import QuestionPartial from './partials/QuestionPartial.vue';
import VideoPartial from './partials/VideoPartial.vue';
import ImagePartial from './partials/ImagePartial.vue';
import UploadPartial from './partials/UploadPartial.vue';
import ReflectionPartial from './partials/ReflectionPartial.vue';
import SlotMachinePartial from './partials/SlotMachinePartial.vue';

export default {
    name: 'AssignmentStep',
    mixins: [ step, notify ],
    components: { HtmlPartial, QuestionPartial, VideoPartial, ImagePartial, UploadPartial, ReflectionPartial, SlotMachinePartial },
    data: function() {
        return {
            stepLoaded: false,
            // flushing promise
            flushing: null,
            loading: false,
        }
    },
    vuex: {
        getters: {},
        actions: {
            completeStep: ({dispatch}, progress, step) => {

                // TODO: name change `completeStep`
                return api.viewedStep(progress.id, step.precedence)
                    .then( response => transform(response.body) )
                    .then( result => dispatch('ADD_PROGRESS', result.progress) )
                    .catch( err => logreject('COULD NOT COMPLETE STEP', err) )

            },
            submitStep: ({ dispatch, state}, progress, step) => {

                return api.submittedStep(progress.id, step.precedence)
                    .then( response => transform(response.body) )
                    .then( result => dispatch('ADD_PROGRESS', result.progress) )
                    .then( () => {
                        const allSubmissionIds = Object.keys(state.submissions.storage)
                            .map( id => state.submissions.storage[id] )
                            .filter(s => s.progress == progress.id )
                            .map(s => s.id)
                        dispatch('FREEZE_SUBMISSIONS', allSubmissionIds)
                    })
                    .catch( err => logreject('COULD NOT SUBMIT STEP', err) )

            },
            finishAssignment: ({ dispatch, state }, progress) => {

                track.event('assignment', 'finish', progress.assignment)
                return api.finishedAssignment(progress.id)
                    .then( response => transform(response.body) )
                    .then( result => dispatch('ADD_PROGRESS', result.progress) )
                    .then( () => {
                        const allSubmissionIds = Object.keys(state.submissions.storage)
                            .map( id => state.submissions.storage[id] )
                            .filter(s => s.progress == progress.id )
                            .map(s => s.id)
                        dispatch('FREEZE_SUBMISSIONS', allSubmissionIds)
                    })
                    .catch( err => logreject('COULD NOT FINISH ASSIGNMENT', err) )

            },
            showTeachertips: ({dispatch}, currentStep) => {
                track.event('teachertips', 'show', currentStep.id)
                dispatch('SHOW_MODAL', 'Teachertips', { step: currentStep })
            }
        }
    },
    computed: {
        // one-based index of step for the current assignment
        currentStepIndex: function() {
            return parseInt(this.$route.params.stepIdx, 10)
        },
        currentStep: function() {
            return this.steps[this.currentStepIndex - 1]
        },
        previousStep: function() {
            const previousStepIndex = this.currentStepIndex - 1

            if ( previousStepIndex < 0 ) {
                return null
            }

            return this.steps[previousStepIndex - 1]

        },
        partials: function() {
            // may not be loaded yet
            if ( this.currentStep.partials === undefined) {
                return []
            }

            return this.currentStep.partials.map( id => this.$store.state.partials.storage[id] )
        },
        prevStepLink: function() {
            const prevStepIndex = this.currentStepIndex - 1;
            return { name: 'assignment.step', params: { assignmentId: this.assignment.id, stepIdx: prevStepIndex }}
        },
        nextStepLink: function() {
            const nextStepIndex = this.currentStepIndex + 1;

            if ( nextStepIndex > this.assignment.steps.length ) {
                const name = this.progress.finished == null ? 'assignment.done' : 'assignment.overview'
                return { name: name, params: { assignmentId: this.assignment.id } }
            }

            return { name: 'assignment.step', params: { assignmentId: this.assignment.id, stepIdx: nextStepIndex }}
        },
        readonly: function() {
            return this.isTeacher || (this.isFrozen && !this.isRetaking)
        },
        isCompleted: function() {
            return this.progress.step >= this.currentStep.precedence
        },
        isFrozen: function() {
            return this.progress.frozen >= this.currentStep.precedence
        },
        isRetaking: function() {
            return this.progress.isRetaking
        },
        isFinished: function() {
            return this.isProgressFinished(this.progress)
        },
        isValid: function() {
            return this.validateStep(this.currentStep, false) // don't check confirm yet
        },
        isLastStep: function() {
            return this.currentStepIndex >= this.assignment.steps.length
        },
        canGoNext: function() {
            return this.isValid
        },
        mustConfirm: function() {
            return (this.confirmable && !this.isFrozen) || (this.isRetaking && this.isLastStep)
        },
        confirmable: function() {
            return this.currentStep.confirm
        },
        buttonText: function() {
            return (this.isLastStep && !this.readonly) ? 'Opsturen' : 'Verder'
        },
        isTeacher: function() {
            return canTeach(this.$store.state.users.current)
        },
        shouldShowTeacherTips: function() {
            return this.isTeacher && this.currentStep.teachertips === true
        },
        canViewStep: function() {
            if ( this.currentStep == null ) {
                console.error('Step not found')
                return false
            }

            if ( !this.isCompleted && this.previousStep && !this.isStepCompleted(this.previousStep) ) {
                console.error('Previous step not completed')
                return false
            }

            return true

        },
    },
    methods: {
        flushAllSubmissions: function() {
            if ( this.$refs.partials == null ) {
                return Promise.resolve()
            }

            return Promise.all( this.$refs.partials.map( p => p.flush() ) )
        },

        complete: function(e, next) {

            if ( this.stealthMode ) {
                return
            }

            e.preventDefault()

            if ( !this.isValid ) {
                return
            }


            const sync = () => {
                this.flushing = this.flushAllSubmissions()
                    .then( () => {
                        if ( this.isLastStep && (!this.isFinished || this.isRetaking) ) {
                            return this.finishAssignment(this.progress)
                        }

                        if ( this.mustConfirm && !this.isFinished ) {
                            return this.submitStep(this.progress, this.currentStep)
                        }

                        if ( !this.isCompleted ) {
                            return this.completeStep(this.progress, this.currentStep)
                        }
                    })
                    .then( next )

                this.flushing.catch( err => {
                    this.loading = false
                    track.exception('Can\'t submit step', err, { step: this.currentStep.id })
                    this.$warn('Kan de oefening niet opsturen')
                }).then( () => this.flushing = null )
            }

            this.loading = true

            if ( this.mustConfirm ) {

                const title = 'Werk inleveren?'
                const message = 'Weet je zeker dat je verder wilt gaan? Hierna kun je jouw tot nu toe ingeleverde werk niet meer aanpassen!'

                this.$alert({ title, message }, [
                    { name: 'Annuleren', action: () => this.loading = false },
                    { name: 'Werk inleveren', action: sync, primary: true },
                ])

            } else {
                sync()
            }

        },


        checkRoute: function(from) {

            if ( !this.canViewStep ) {
                if ( from != null ) {
                    this.$router.back()
                } else {
                    this.autoRedirectStep(this.progress)
                }
            }

        },

        makeSureSubmissionsAreSubmitted: function(next) {

            this.loading = true

            // flushes all debounced submissions before going to the next step
            const flushing = this.flushing || this.flushAllSubmissions()

            flushing.catch(() => {}).then( () => this.flushing = null )

            flushing.then(
                next,
                err => {
                    this.loading = false
                    track.exception('Can\'t flush submissions', err, { step: this.currentStep.id })

                    this.$alert({ title: 'Kan werk niet opslaan!', message: 'Jouw gemaakte werk kan op dit moment niet worden opgeslagen. Je kunt dit bericht annuleren en het later opnieuw proberen, of doorgaan en je gemaakte werk gaat verloren.' }, [
                        { name: 'Annuleren', action: () => next(false)},
                        { name: 'Ongedaan maken & doorgaan', danger: true, action: next }
                    ])
                }
            )

        }
    },
    created: function() {
        this.checkRoute()
    },
    watch: {
        '$route': function(to, from) {
            this.checkRoute(from)
        }
    },
    beforeRouteUpdate: function(to, from, next)  {
        to.params._immediate === true ? next() : this.makeSureSubmissionsAreSubmitted(next)
    },
    beforeRouteLeave: function(to, from, next) {
        to.params._immediate === true ? next() : this.makeSureSubmissionsAreSubmitted(next)
    },

}
</script>


<style lang="scss">

    @import 'global';

</style>



// WEBPACK FOOTER //
// Step.vue?133b2ff0